import { AlertTriangle, X, Loader2 } from "lucide-react";
import type { Proposal, StockRow } from "../api/types";
import { EntityLabels } from "./EntityLabels";

interface Props {
  stock: StockRow[];
  pending: Proposal[];
  onReject: (proposalId: string) => Promise<unknown> | void;
  rejecting?: string | null;
  /** Required for EntityLabels — labels are per-organisation. */
  organisation: string;
  /** Panneau id — needed so EntityLabels can validate/refuse pending
   *  `label_for_entity` proposals that target produits in this panneau. */
  panneauId?: string;
}

/**
 * Contenu of an `inventaire` panneau.
 *
 * Two stacked sections: the agent's pending mouvement proposals at the
 * top (with per-row Refuser; Valider is the panneau-level button), then
 * the current stock snapshot below grouped by category.
 */
export function InventaireTableau({
  stock,
  pending,
  onReject,
  rejecting,
  organisation,
  panneauId,
}: Props) {
  // Group stock by category for nicer display
  const byCategory = stock.reduce<Record<string, StockRow[]>>((acc, row) => {
    (acc[row.category] ??= []).push(row);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Pending mouvements section */}
      {pending.length > 0 ? (
        <section>
          <h3 className="text-xs uppercase tracking-wide text-muted dark:text-muted-dark mb-2">
            Mouvements proposés ({pending.length})
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted dark:text-muted-dark font-normal border-b border-black/10 dark:border-white/10">
                <th className="py-2 pr-4 font-normal">Produit</th>
                <th className="py-2 pr-4 font-normal text-right">+ / –</th>
                <th className="py-2 pr-4 font-normal">Motif</th>
                <th
                  className="py-2 pr-4 font-normal"
                  title="Phrase du message qui a généré cette proposition"
                >
                  Origine
                </th>
                <th className="py-2 pr-2 font-normal text-right"></th>
              </tr>
            </thead>
            <tbody>
              {pending.map((p) => {
                const pl = p.payload;
                if (p.type !== "inventory_movement") return null;
                const delta = Number(pl.delta ?? 0);
                const isRejecting = rejecting === p.id;
                // The delta is stored in the canonical unit. Look it up
                // from the stock snapshot — every produit the panneau
                // tracks is in `stock`, so a missing match is a config
                // bug worth surfacing as "?".
                const canonicalUnit =
                  stock.find((s) => s.canonical_id === pl.canonical_id)
                    ?.unit ?? "?";
                const unitHint = String(pl.unit_hint ?? "").trim();
                // Mismatch heuristic: if the operator cited a unit
                // that doesn't lexically include the canonical unit
                // (e.g. "3 paquets" with canonical_unit="kg"), flag it.
                // Numbers in unit_hint are stripped before the check
                // since "3 kg" should match unit "kg".
                const hintTokens = unitHint
                  .toLowerCase()
                  .replace(/[\d.,]/g, " ")
                  .split(/\s+/)
                  .filter(Boolean);
                const unitMismatch =
                  unitHint !== "" &&
                  canonicalUnit !== "?" &&
                  !hintTokens.includes(canonicalUnit.toLowerCase());
                return (
                  <tr key={p.id} className="border-b border-black/5 dark:border-white/10 last:border-0">
                    <td className="py-2 pr-4 font-medium">
                      {String(pl.canonical_id ?? "")}
                    </td>
                    <td
                      className={`py-2 pr-4 text-right tabular-nums font-semibold ${delta >= 0 ? "text-chip-green" : "text-chip-red"}`}
                    >
                      <span className="inline-flex items-baseline gap-1 justify-end">
                        <span>
                          {delta >= 0 ? "+" : ""}
                          {delta}
                        </span>
                        <span className="text-xs font-normal text-muted dark:text-muted-dark">
                          {canonicalUnit}
                        </span>
                        {unitMismatch && (
                          <span
                            title={`L'utilisateur a dit « ${unitHint} » mais l'unité canonique est ${canonicalUnit}. Vérifie la conversion avant de valider.`}
                            className="text-chip-orange"
                          >
                            <AlertTriangle className="h-3 w-3" aria-label="Unité incohérente" />
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-muted dark:text-muted-dark">{String(pl.reason ?? "")}</td>
                    <td className="py-2 pr-4 text-muted dark:text-muted-dark italic">
                      « {p.source_quote} »
                    </td>
                    <td className="py-2 pr-2 text-right">
                      <button
                        type="button"
                        onClick={() => onReject(p.id)}
                        disabled={isRejecting}
                        title="Refuser ce mouvement"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-40"
                        aria-label={`Refuser ${pl.canonical_id}`}
                      >
                        {isRejecting ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                        Refuser
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      ) : null}

      {/* Current stock snapshot */}
      <section>
        <h3 className="text-xs uppercase tracking-wide text-muted dark:text-muted-dark mb-2">
          État du stock
        </h3>
        {stock.length === 0 ? (
          <div className="text-muted dark:text-muted-dark text-sm py-4">
            Aucun produit dans ce panneau. La configuration peut filtrer par
            catégorie ou liste d'produits — ajustez <code>config.canonical_ids</code>.
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(byCategory).map(([cat, rows]) => (
              <div key={cat}>
                <div className="text-xs text-muted dark:text-muted-dark mb-1.5 capitalize">{cat}</div>
                <table className="w-full text-sm">
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.canonical_id} className="border-b border-black/5 dark:border-white/10 last:border-0">
                        <td className="py-1.5 pr-3">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span>{r.name_fr}</span>
                            <EntityLabels
                              kind="produit"
                              entityId={r.canonical_id}
                              organisation={organisation}
                              compact
                              pending={pending}
                              panneauId={panneauId}
                            />
                          </div>
                        </td>
                        <td className="py-1.5 pr-2 text-right tabular-nums font-semibold w-16">
                          {r.stock}
                        </td>
                        <td className="py-1.5 pr-2 text-xs text-muted dark:text-muted-dark w-12">
                          {r.unit}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </section>

      {pending.length === 0 && stock.length === 0 ? (
        <div className="text-muted dark:text-muted-dark text-sm">
          Tapez un message à l'agent en haut de l'écran pour ajouter un mouvement.
        </div>
      ) : null}
    </div>
  );
}
